import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {Link, useNavigate, useParams} from "react-router-dom";
import {LoggedInView} from "../common/LoggedInView";
import Breadcrumbs from "../common/Breadcrumbs";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {RequestStatus} from "../../utils/Enums";
import {getUsers} from "../../store/reducers/UsersSlice";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import {clearCurrentInquiry, createInquiry, getCurrentInquiry, resetCreateInquiryStatus} from "../../store/reducers/InquiriesSlice";
import {getCountries} from "../../store/reducers/CountriesSlice";
import {getServices} from "../../store/reducers/ServicesSlice";
import RouteInput from "./components/Routenput";
import ClientInput from "../common/form/ClientInput";
import ElogTextarea from "../common/form/ElogTextarea";
import WeightInput from "../common/form/WeightInput";
import {FormikProvider, useFormik, Form} from "formik";
import {
    getCopiedInquiryInitialValues,
    isInquiryCopyDraftForSource
} from "./utils/getCopiedInquiryInitialValues";

interface NewInquiryScreenProps {
}

export interface NewInquiryFormProps {
    user: number;
    expires_at: string;
}

export const NewInquiryScreen: React.FC<NewInquiryScreenProps> = () => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {id} = useParams();
    const isCopy = Boolean(id);
    const STORAGE_KEY = isCopy ? `newInquiryCopy:${id}` : 'newInquiryForm';

    const inquiriesState = useAppSelector((state) => state.inquiries);
    const usersState = useAppSelector((state) => state.users);
    const countriesState = useAppSelector(state => state.countries);
    const servicesState = useAppSelector((state) => state.services);
    const infoState = useAppSelector(state => state.info);

    const [inquiryUser, setInquiryUser] = useState<string>('');
    const [initialValuesLoaded, setInitialValuesLoaded] = useState(!isCopy);

    const [initialValues, setInitialValues] = useState(() => {
        const saved = isCopy ? null : sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            parsed.client = parsed.client || '';  // Ensure client is properly initialized
            return { ...parsed, _hydrated: true };
        }
        return {
            startCountry: '45',
            destinationCountry: '45',
            service: '',  // Set to empty initially until serviceOptions are loaded
            user: '0',
            client: '',  // Ensure client is initialized
            clientContact: '',
            comment: '',
            amount: '',
            weight: '',
            noWeight: false,
            _hydrated: true
        };
    });

    const requestStatus: RequestStatus = inquiriesState.createInquiryStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: "Päringud", url: "/inquiries"},
        {text: isCopy ? "Päringu kopeerimine" : "Päringu lisamine", url: isCopy ? `/inquiries/${id}/copy` : "/inquiries/new"},
    ];

    useEffect(() => {
        if (requestStatus === RequestStatus.Success) {

            const inquiryUser = inquiriesState?.currentInquiry?.user?.firstname ?? '';
            const inquiryUserId = inquiriesState?.currentInquiry?.user?.id;
            const currentUserId = infoState?.me?.id;

            dispatch(resetCreateInquiryStatus());

            if (inquiryUserId === currentUserId) {
                navigate(breadcrumbLinks[0].url);
            } else {
                setInquiryUser(inquiryUser);
            }

        }
    }, [requestStatus]);

    useEffect(() => {
        dispatch(getCountries());
        dispatch(getServices(false));
        dispatch(getUsers());
    }, []);

    useEffect(() => {
        if (!isCopy || !id) {
            setInitialValuesLoaded(true);
            return;
        }

        let active = true;
        setInitialValuesLoaded(false);

        dispatch(getCurrentInquiry({id: parseInt(id)}))
            .unwrap()
            .then((inquiry) => {
                if (!active) return;

                let copiedValues = getCopiedInquiryInitialValues(inquiry);
                const saved = sessionStorage.getItem(STORAGE_KEY);

                if (saved) {
                    try {
                        const savedValues = JSON.parse(saved);
                        if (isInquiryCopyDraftForSource(savedValues, id)) {
                            copiedValues = savedValues;
                        }
                    } catch {
                        sessionStorage.removeItem(STORAGE_KEY);
                    }
                }

                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(copiedValues));
                setInitialValues(copiedValues);
                setInitialValuesLoaded(true);
            })
            .catch(() => {
                if (active) setInitialValuesLoaded(true);
            });

        return () => {
            active = false;
            dispatch(clearCurrentInquiry());
        };
    }, [dispatch, id, isCopy, STORAGE_KEY]);

    const userOptions: Option[] = usersState.users
        .filter((user) => user.logistician === 1 || user.is_checker === 1)
        .map((user) => ({
            value: user.id.toString(),
            label: `${user.firstname} ${user.lastname}`,
        }));

    userOptions.unshift({
        value: "0",
        label: "Tavajaotus",
    });

    const countryOptions: Option[] = countriesState.countries.map((country) => ({
        value: country.id.toString(),
        label: country.name,
    }));

    const serviceOptions: Option[] = servicesState.services.map((service) => ({
        value: service.id.toString(),
        label: service.name,
    }));


    useEffect(() => {
        if (serviceOptions.length > 0 && !initialValues.service) {
            setInitialValues((prev: typeof initialValues) => ({
                ...prev,
                service: serviceOptions[0].value,
            }));
        }
    }, [serviceOptions]);

    const formik = useFormik({
        initialValues,
        onSubmit: (values: any) => {
            sessionStorage.removeItem(STORAGE_KEY);
            const {_copySourceId, _copyDraftVersion, _hydrated, ...inquiryValues} = values;
            dispatch(createInquiry(inquiryValues));
        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    useEffect(() => {
        if (!initialValuesLoaded) return;

        const stored = sessionStorage.getItem(STORAGE_KEY);
        let parsed = stored ? JSON.parse(stored) : {};
        parsed = { ...values };
        if (!parsed._hydrated) parsed._hydrated = true;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    }, [values, initialValuesLoaded, STORAGE_KEY]);

    if (!initialValuesLoaded) return null;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {inquiryUser.length > 0 && <div className="message msg-ok">
                <div>
                    <h2>Päring lisatud:</h2><br/>
                    Sellega tegeleb <strong>{inquiryUser}</strong>
                </div>
            </div>}
            {requestStatus === RequestStatus.Failed && inquiriesState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {inquiriesState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>
                            <span>{isCopy ? 'Päringu kopeerimine' : 'Päringu andmed'}</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <RouteInput countries={countryOptions}/>
                            <ClientInput
                                storageKey={STORAGE_KEY}
                                initialClientContact={isCopy ? initialValues.clientContact : undefined}
                            />
                            <ElogSelect label="Teenuse liik" name="service" options={serviceOptions} mandatory={true}/>
                            <ElogTextarea label="Kommentaar" name="comment" mandatory={false}/>
                            <ElogTextarea label="Kaubakogus" name="amount" mandatory={true}/>
                            <WeightInput/>
                            <ElogSelect label="Logistik" name="user" options={userOptions} mandatory={true}/>
                            </tbody>
                        </table>
                    </div>
                    <div className="action clear">
                        <p>
                            <button type="submit">
                                  <span>
                                    <strong>Salvesta</strong>
                                  </span>
                            </button>
                            <Link to="/inquiries" onClick={() => sessionStorage.removeItem(STORAGE_KEY)}>Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    )

}
